"use client";

import { useCallback, useState, type ComponentProps } from "react";
import { usePathname } from "next/navigation";
import clsx from "clsx";
import HomeIcon from "@heroicons/react/24/outline/HomeIcon";
import HomeSelectedIcon from "@heroicons/react/20/solid/HomeIcon";
import StarIcon from "@heroicons/react/24/outline/StarIcon";
import StarSelectedIcon from "@heroicons/react/24/solid/StarIcon";
import ArchiveBoxIcon from "@heroicons/react/24/outline/ArchiveBoxIcon";
import ArchiveBoxSelectedIcon from "@heroicons/react/24/solid/ArchiveBoxIcon";
import TagIcon from "@heroicons/react/24/outline/TagIcon";
import TagSelectedIcon from "@heroicons/react/24/solid/TagIcon";
import HeartIcon from "@heroicons/react/24/outline/HeartIcon";
import HeartSelectedIcon from "@heroicons/react/24/solid/HeartIcon";
import CalendarIcon from "@heroicons/react/24/outline/CalendarIcon";
import CalendarSelectedIcon from "@heroicons/react/24/solid/CalendarIcon";
import UserGroupIcon from "@heroicons/react/24/outline/UserGroupIcon";
import UserGroupSelectedIcon from "@heroicons/react/24/solid/UserGroupIcon";
import {
  PeopleDirectoryIcon,
  PeopleDirectorySelectedIcon,
} from "../Icons/UserWithLinesIcon";
import NavItem from "./NavItem";
import NavHr from "./NavHr";
import NavLink from "./NavLink";
import RssPopover from "../RssPopover";

const items = [
  {
    title: "Home",
    href: "/",
    UnselectedIcon: HomeIcon,
    SelectedIcon: HomeSelectedIcon,
  },
  {
    title: "Best of the Forum",
    href: "/best-of",
    UnselectedIcon: StarIcon,
    SelectedIcon: StarSelectedIcon,
  },
  {
    title: "All posts",
    href: "/all-posts",
    UnselectedIcon: ArchiveBoxIcon,
    SelectedIcon: ArchiveBoxSelectedIcon,
  },
  {
    title: "Topics",
    href: "/topics",
    UnselectedIcon: TagIcon,
    SelectedIcon: TagSelectedIcon,
  },
  {
    title: "People directory",
    href: "/people-directory",
    UnselectedIcon: PeopleDirectoryIcon,
    SelectedIcon: PeopleDirectorySelectedIcon,
  },
  {
    title: "Take action",
    href: "/topics/opportunities-to-take-action",
    UnselectedIcon: HeartIcon,
    SelectedIcon: HeartSelectedIcon,
    loggedOutOnly: true,
  },
  {
    title: "Events",
    href: "/events",
    UnselectedIcon: CalendarIcon,
    SelectedIcon: CalendarSelectedIcon,
  },
  {
    title: "Groups directory",
    href: "/groups",
    UnselectedIcon: UserGroupIcon,
    SelectedIcon: UserGroupSelectedIcon,
  },
] satisfies Omit<ComponentProps<typeof NavItem>, "isSelected">[];

const links = [
  {
    title: "How to use the Forum",
    href: "/about",
  },
  {
    title: "Forum events calendar",
    href: "https://calendar.google.com/calendar/u/0?cid=Y182M2YxZTI4NmJkNWQyMDYwNjQ1MjRjNTExMWVlYTRjOGM5M2JkZjFkYmNiMmZiOGViZmU5NWIzNzQ2YWViOTYwQGdyb3VwLmNhbGVuZGFyLmdvb2dsZS5jb20",
  },
  {
    title: "EA Handbook",
    href: "/handbook",
  },
  {
    title: "EA Forum Podcast",
    href: "/posts/K5Snxo5EhgmwJJjR2/announcing-ea-forum-podcast-audio-narrations-of-ea-forum",
  },
  {
    title: "Quick takes",
    href: "/quicktakes",
  },
  {
    title: "RSS",
  },
  {
    title: "Cookie policy",
    href: "/cookie-policy",
  },
  {
    title: "Copyright",
    href: "/copyright",
  },
] satisfies Pick<ComponentProps<typeof NavLink>, "title" | "href">[];

export default function Nav({ className }: Readonly<{ className?: string }>) {
  const [rssOpen, setRssOpen] = useState(false);
  const onOpenRss = useCallback(() => setRssOpen(true), []);
  const onCloseRss = useCallback(() => setRssOpen(false), []);
  const pathname = usePathname();
  return (
    <nav className={clsx("w-[200px]", className)} data-component="Nav">
      <div className="w-[min-content]">
        {items.map(({ href, ...props }) => (
          <NavItem
            key={href}
            href={href}
            isSelected={pathname === href}
            {...props}
          />
        ))}
        <NavHr />
        {links.map(({ title, href }) => (
          <NavLink
            key={title}
            title={title}
            href={href}
            onClick={title === "RSS" ? onOpenRss : undefined}
            isSelected={pathname === href}
          />
        ))}
        <NavHr />
        <NavLink
          title="Contact us"
          href="/contact"
          isSelected={pathname === "/contact"}
        />
      </div>
      <RssPopover open={rssOpen} onClose={onCloseRss} />
    </nav>
  );
}
